import { Sport } from '../types';

// Tab configuration
export interface DepthChartTab {
  id: string;
  label: string;
  defaultValue?: string; // For optional dropdown values
}

// Depth string configuration (1st string, 2nd string, etc.)
export interface DepthStringConfig {
  defaultLabels: {id: number, name: string}[];
  sportSpecificName?: string; // e.g., "Lines" for hockey, "Subs" for soccer
}

// Formation configuration
export interface FormationConfig {
  enabled: boolean;
  label?: string;
  sportSpecificFormations?: boolean; // Whether to use sport-specific formations
  defaultFormation?: string;
}

// Print configuration
export interface PrintConfig {
  stylesheets: string[]; // Array of CSS file paths to load
  layout: 'standard' | 'horizontal' | 'combined'; // Layout type
  customLayoutFunction?: string; // Name of custom layout function if needed
}

// Sport-specific ruleset for depth chart
export interface SportRuleset {
  tabs: DepthChartTab[];
  depthStrings: DepthStringConfig;
  formations: FormationConfig;
  showSpecialTeams: boolean;
  specialTeamsUnits?: string[];
  // Additional sport-specific settings
  useInnings?: boolean;
  defaultInnings?: number;
  inningRanges?: {min: number; max: number}; // Min/max number of innings
  // Print configuration
  print: PrintConfig;
  /** Overrides the "Game(s)" wording in nav/UI copy for sports that call their events something else. */
  eventLabel?: { singular: string; plural: string; verb: string };
}

// Define rulesets for all sports
export const SPORT_RULESETS: Record<Sport, SportRuleset> = {
  Football: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' },
      { id: 'Special Teams', label: 'Special Teams' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st String'},
        {id: 2, name: '2nd String'},
        {id: 3, name: '3rd String'}
      ]
    },
    formations: {
      enabled: true,
      label: 'Formation',
      sportSpecificFormations: true,
      defaultFormation: 'Spread'
    },
    showSpecialTeams: true,
    specialTeamsUnits: ['Kickoff', 'Kick Return', 'Punt', 'Punt Return', 'Field Goal'],
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/football-print.css'],
      layout: 'horizontal',
      customLayoutFunction: 'renderFootballLayout'
    }
  },
  
  FlagFootball: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st String'},
        {id: 2, name: '2nd String'},
        {id: 3, name: '3rd String'}
      ]
    },
    formations: {
      enabled: true,
      label: 'Formation',
      sportSpecificFormations: true,
      defaultFormation: 'Spread'
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Basketball: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Rotation'},
        {id: 3, name: 'Bench'}
      ],
      sportSpecificName: 'Unit'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Baseball: {
    tabs: [
      { id: 'Lineup', label: 'Lineup' },
      { id: 'Fielding', label: 'Fielding' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Backups'}
      ]
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    useInnings: true,
    defaultInnings: 7,
    inningRanges: {min: 1, max: 9},
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/baseball-print.css'],
      layout: 'combined',
      customLayoutFunction: 'renderBaseballLayout'
    }
  },
  
  Softball: {
    tabs: [
      { id: 'Lineup', label: 'Lineup' },
      { id: 'Fielding', label: 'Fielding' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Backups'}
      ]
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    useInnings: true,
    defaultInnings: 7,
    inningRanges: {min: 1, max: 7},
    print: {
      stylesheets: ['/src/styles/print/base-print.css', '/src/styles/print/baseball-print.css'],
      layout: 'combined',
      customLayoutFunction: 'renderBaseballLayout'
    }
  },
  
  Soccer: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starting XI'},
        {id: 2, name: 'Substitutes'}
      ],
      sportSpecificName: 'Selection'
    },
    formations: {
      enabled: true,
      label: 'Formation',
      defaultFormation: '4-4-2'
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Volleyball: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starters'},
        {id: 2, name: 'Rotation'}
      ],
      sportSpecificName: 'Rotation'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Hockey: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st Line'},
        {id: 2, name: '2nd Line'},
        {id: 3, name: '3rd Line'},
        {id: 4, name: '4th Line'}
      ],
      sportSpecificName: 'Lines'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },
  
  Lacrosse: {
    tabs: [
      { id: 'Offense', label: 'Offense' },
      { id: 'Defense', label: 'Defense' },
      { id: 'Midfield', label: 'Midfield' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: '1st Line'},
        {id: 2, name: '2nd Line'}
      ],
      sportSpecificName: 'Lines'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    }
  },

  Tennis: {
    tabs: [
      { id: 'Team', label: 'Team' }
    ],
    depthStrings: {
      defaultLabels: [
        {id: 1, name: 'Starter'},
        {id: 2, name: 'Backup'}
      ],
      sportSpecificName: 'Depth'
    },
    formations: {
      enabled: false
    },
    showSpecialTeams: false,
    print: {
      stylesheets: ['/src/styles/print/base-print.css'],
      layout: 'standard'
    },
    eventLabel: { singular: 'Match', plural: 'Matches', verb: 'Score' }
  }
};

// Helper function to get default depth strings for a sport
export function getDefaultDepthStrings(sport: Sport): {id: number; name: string; active: boolean}[] {
  const sportConfig = SPORT_RULESETS[sport];
  return sportConfig.depthStrings.defaultLabels.map(label => ({
    ...label,
    active: true
  }));
}

// Helper function to initialize depth strings for all tabs
export function initializeDepthStrings(sport: Sport): Record<string, {id: number; name: string; active: boolean}[]> {
  const sportConfig = SPORT_RULESETS[sport];
  const result: Record<string, {id: number; name: string; active: boolean}[]> = {};
  
  sportConfig.tabs.forEach(tab => {
    result[tab.id] = getDefaultDepthStrings(sport);
  });
  
  return result;
}

// Helper to determine if a sport uses offense/defense tabs
export function usesOffenseDefenseTabs(sport: Sport): boolean {
  const tabs = SPORT_RULESETS[sport].tabs;
  return tabs.some(tab => tab.id === 'Offense') && tabs.some(tab => tab.id === 'Defense');
}

const DEFAULT_EVENT_LABEL = { singular: 'Game', plural: 'Games', verb: 'Track' };

/** Sport-appropriate word for what the /games section tracks (e.g. "Matches" for Tennis). */
export function getEventLabel(sport: Sport | string | undefined | null): { singular: string; plural: string; verb: string } {
  const ruleset = sport ? SPORT_RULESETS[sport as Sport] : undefined;
  return ruleset?.eventLabel ?? DEFAULT_EVENT_LABEL;
}

/**
 * Sports with game tracking (the /games section).
 *
 * Single source of truth — previously this list was spelled out separately in
 * the sidebar, the mobile nav, the team hub and GamesManagement, which is how
 * they drifted. Add a sport here once and every entry point follows.
 */
export const SPORTS_WITH_GAMES: ReadonlySet<Sport> = new Set<Sport>([
  'Football',
  'FlagFootball',
  'Baseball',
  'Softball',
  'Tennis',
]);

/** Whether this sport has the games/schedule section enabled. */
export function hasGames(sport: Sport | string | undefined | null): boolean {
  return SPORTS_WITH_GAMES.has(sport as Sport);
}

/**
 * Sports a personal (parent/player) account can pick at /welcome or later in
 * Settings — the subset of SPORTS_WITH_GAMES, since match tracking is the
 * whole point of a personal account.
 */
export const INDIVIDUAL_SPORTS: Sport[] = ['Tennis', 'Football', 'FlagFootball', 'Baseball', 'Softball'];

/** Mirrors TeamSetup.tsx's per-sport format default. */
export const FORMAT_BY_SPORT: Record<Sport, string> = {
  Football: '11v11',
  FlagFootball: '7v7',
  Basketball: '5v5',
  Soccer: '11v11',
  Baseball: '9v9',
  Softball: '10v10',
  Hockey: '6v6',
  Volleyball: '6v6',
  Lacrosse: '10v10',
  Tennis: 'Singles',
};